<script setup>
import { reactive, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { cortexToolsAPI } from 'dashboard/api/cortex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';

const emit = defineEmits(['created', 'cancel']);
const { t } = useI18n();
const isSaving = ref(false);

const form = reactive({
  name: '',
  description: '',
  tool_type: 'http_api',
  risk_level: 'low',
});

const toolTypeOptions = computed(() => [
  { value: 'http_api', label: 'HTTP API' },
  { value: 'native', label: 'Native' },
  { value: 'crawler', label: 'Crawler' },
  { value: 'storage', label: 'Storage' },
]);

const riskOptions = computed(() => [
  { value: 'low', label: t('CORTEX.TOOLS.RISK.low') },
  { value: 'medium', label: t('CORTEX.TOOLS.RISK.medium') },
  { value: 'high', label: t('CORTEX.TOOLS.RISK.high') },
]);

const canSubmit = computed(() => form.name.trim().length > 0);

const createTool = async () => {
  if (!canSubmit.value || isSaving.value) return;
  isSaving.value = true;
  try {
    const { data } = await cortexToolsAPI.create({
      tool: {
        name: form.name.trim(),
        description: form.description.trim(),
        tool_type: form.tool_type,
        risk_level: form.risk_level,
        status: 'draft',
      },
    });
    emit('created', data);
  } catch {
    useAlert(t('CORTEX.TOOLS.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    class="p-4 space-y-3 border border-dashed rounded-lg border-n-brand/30 bg-n-brand/5"
  >
    <div class="flex items-center justify-between">
      <h4 class="text-xs font-semibold text-n-brand uppercase">
        {{ t('CORTEX.AGENTS.WIZARD.TOOL_CREATE_NEW') }}
      </h4>
      <button
        type="button"
        class="p-1 rounded hover:bg-n-alpha-2"
        @click="emit('cancel')"
      >
        <span class="i-lucide-x size-4 text-n-slate-10" />
      </button>
    </div>
    <div class="grid grid-cols-2 gap-3">
      <div>
        <label class="block text-xs font-medium text-n-slate-10 mb-1">
          {{ t('CORTEX.AGENTS.WIZARD.TOOL_INLINE_NAME') }}
        </label>
        <NextInput
          v-model="form.name"
          class="w-full"
          :placeholder="t('CORTEX.TOOLS.FORM.NAME_PLACEHOLDER')"
          :message="t('CORTEX.AGENTS.WIZARD.TOOL_INLINE_NAME_HELP')"
          required
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-10 mb-1">
          {{ t('CORTEX.AGENTS.WIZARD.TOOL_INLINE_DESC') }}
        </label>
        <NextInput
          v-model="form.description"
          class="w-full"
          :placeholder="t('CORTEX.TOOLS.FORM.DESCRIPTION_PLACEHOLDER')"
          :message="t('CORTEX.AGENTS.WIZARD.TOOL_INLINE_DESC_HELP')"
        />
      </div>
    </div>
    <div class="grid grid-cols-2 gap-3">
      <div>
        <label class="block text-xs font-medium text-n-slate-10 mb-1">
          {{ t('CORTEX.AGENTS.WIZARD.TOOL_INLINE_TYPE') }}
        </label>
        <AdaptiveSelect
          v-model="form.tool_type"
          :options="toolTypeOptions"
          :message="t('CORTEX.AGENTS.WIZARD.TOOL_INLINE_TYPE_HELP')"
          class="w-full"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-10 mb-1">
          {{ t('CORTEX.AGENTS.WIZARD.TOOL_INLINE_RISK') }}
        </label>
        <AdaptiveSelect
          v-model="form.risk_level"
          :options="riskOptions"
          :message="t('CORTEX.AGENTS.WIZARD.TOOL_INLINE_RISK_HELP')"
          class="w-full"
        />
      </div>
    </div>
    <NextButton
      type="button"
      blue
      sm
      :is-loading="isSaving"
      :disabled="!canSubmit"
      @click="createTool"
    >
      <span class="i-lucide-plus size-4" />
      {{ t('CORTEX.AGENTS.WIZARD.TOOL_INLINE_SUBMIT') }}
    </NextButton>
  </div>
</template>
